import { version } from "../../../package.json";
import store from "../../app/store";
import {
  ANALYTICS_MODE,
  TINYBIRD_CHANNEL,
  TINYBIRD_ENDPOINT,
  TINYBIRD_TOKEN,
} from "../../constants";

const GATEWAY_PATH = "/api/v1/map_events";

// JWT getter injected by the SitumAPIProvider so this plain util can read the
// canonical SDK JWT (used in gateway mode).
let getJWTRef: (() => string | undefined) | null = null;
export const setEventEmitterJWTGetter = (
  getter: (() => string | undefined) | null,
) => {
  getJWTRef = getter;
};

type ActionTypes =
  | "action_geofence_entered"
  | "select_building"
  | "poi_selected"
  | "poi_category_selected"
  | "route_request"
  | "navigation_request"
  | "navigation_reached"
  | "navigation_cancel"
  | "search"
  | "app_start"
  | "session_started"
  | "session_ended"
  | "feedback_submitted";
interface Event {
  type: ActionTypes;
  message: string;
}

interface AnalyticsEvent {
  timestamp: string;
  session_id: string;
  organization_id: string;
  app_name: string;
  user_agent: string;
  profile?: string;
  event: Event;
}

interface locationRequest {
  building_id: number;
  floor_id: number | null;
  lat: number | null;
  lng: number | null;
}

interface NavigationRouteRequest {
  type: "location-based" | "location-independent";
  origin: number;
  destination: number;
  location: locationRequest;
}

export const emmitEventAppStarted = (
  buildingId: number,
  profileName: string | null,
) => {
  const message = {
    building_id: buildingId,
    profile_name: profileName,
  };
  emmitEventToAnalytics({
    message: prepareMessage(message),
    type: "app_start",
  });
};

export const emmitEventSessionStarted = (
  buildingId: number,
  profileName: string | null,
) => {
  const message = {
    building_id: buildingId,
    profile_name: profileName,
  };
  emmitEventToAnalytics({
    message: prepareMessage(message),
    type: "session_started",
  });
};

export const emmitEventSessionEnded = (
  buildingId: number,
  profileName: string | null,
) => {
  const message = {
    building_id: buildingId,
    profile_name: profileName,
  };
  emmitEventToAnalytics({
    message: prepareMessage(message),
    type: "session_ended",
  });
};

export const emmitEventPoiSelected = (poiId: number | null) => {
  if (poiId === null) {
    return;
  }
  const message = { poi_id: poiId };
  emmitEventToAnalytics({
    message: prepareMessage(message),
    type: "poi_selected",
  });
};

export const emmitEventPoiCategorySelected = ({
  poiCategoryId,
  buildingId,
}: {
  poiCategoryId: number | null;
  buildingId: number | null;
}) => {
  if (poiCategoryId === null) {
    return;
  }
  const message = { building_id: buildingId, poi_category_id: poiCategoryId };
  emmitEventToAnalytics({
    message: prepareMessage(message),
    type: "poi_category_selected",
  });
};

export const emmitEventSearched = ({
  term,
  buildingId,
}: {
  term: string | null;
  buildingId: number | null;
}) => {
  if (term === null) {
    return;
  }
  const message = { building_id: buildingId, term: term.trimEnd() };
  emmitEventToAnalytics({
    message: prepareMessage(message),
    type: "search",
  });
};

export const emmitEventRouteRequested = (
  routeRequested: NavigationRouteRequest,
) => {
  const message = routeRequested;
  emmitEventToAnalytics({
    message: prepareMessage(message),
    type: "route_request",
  });
};

export const emmitEventNavigationRequested = (
  navigationRequested: NavigationRouteRequest,
) => {
  const message = navigationRequested;
  emmitEventToAnalytics({
    message: prepareMessage(message),
    type: "navigation_request",
  });
};

export const emmitEventNavigationFinished = (
  poiOrigin: number,
  poiDestination: number,
) => {
  const message = { destination: poiDestination, origin: poiOrigin };
  emmitEventToAnalytics({
    message: prepareMessage(message),
    type: "navigation_reached",
  });
};

export const emmitEventNavigationCanceled = (
  poiOrigin: number,
  poiDestination: number,
) => {
  const message = { destination: poiDestination, origin: poiOrigin };
  emmitEventToAnalytics({
    message: prepareMessage(message),
    type: "navigation_cancel",
  });
};

export const emmitEventGeofenceEntered = (geofenceId: string) => {
  const message = { geofenceId };
  emmitEventToAnalytics({
    message: prepareMessage(message),
    type: "action_geofence_entered",
  });
};

export const emmitFeedbackEvent = async ({
  buildingId,
  feedbackText,
  profileName,
  rating,
  deviceId,
}: {
  buildingId: number;
  feedbackText: string;
  profileName: string | null;
  rating: number;
  deviceId: string;
}) => {
  const message = {
    building_id: buildingId,
    device_id: deviceId,
    feedback_text: feedbackText,
    profile_name: profileName,
    rating,
  };
  await emmitEventToAnalytics({
    message: prepareMessage(message),
    type: "feedback_submitted",
  });
};

type AnalyticsTransport = { url: string; bearer: string };

// Resolve the transport (URL + bearer) for the current analytics mode.
// Only "gateway" and "off" are special-cased; anything else (including unset,
// "", null, "tinybird") falls back to the legacy Tinybird transport, which
// already self-disables when its env vars are missing.
const resolveTransport = (): AnalyticsTransport | null => {
  if (ANALYTICS_MODE === "off") {
    return null;
  }

  if (ANALYTICS_MODE === "gateway") {
    const domain = store.getState().config.auth?.domain;
    if (!domain) {
      console.debug("analytics: gateway mode but auth.domain is empty");
      return null;
    }
    const bearer = getJWTRef?.();
    if (!bearer) {
      console.debug("analytics: gateway mode but no JWT available");
      return null;
    }
    return { bearer, url: `https://${domain}${GATEWAY_PATH}` };
  }

  // Default (tinybird): legacy direct-to-Tinybird path. Self-disables when
  // any of endpoint/channel/token is missing — covers on-premise builds that
  // simply leave the Tinybird vars empty.
  if (!TINYBIRD_ENDPOINT || !TINYBIRD_CHANNEL || !TINYBIRD_TOKEN) {
    console.debug(
      "analytics: tinybird not configured");
    return null;
  }
  const url = new URL(TINYBIRD_ENDPOINT);
  url.searchParams.set("name", TINYBIRD_CHANNEL);
  return { bearer: TINYBIRD_TOKEN, url: url.toString() };
};

const emmitEventToAnalytics = async (event: Event) => {
  const transport = resolveTransport();
  if (!transport) return;

  const state = store.getState();
  if (!state.app.currentOrganization) {
    throw new Error("Organization not found");
  }

  const payload: AnalyticsEvent = {
    app_name: state.config.internal.appName,
    event,
    organization_id: state.app.currentOrganization?.id || "undefined",
    ...(state.session.profileName
      ? { profile: state.session.profileName }
      : {}),
    session_id: state.session.sessionId,
    timestamp: new Date().toISOString(),
    user_agent: `SitumMapViewer/${version} (${
      state.config.internal.embed ? "Embed" : "Web"
    }) ${window.navigator.userAgent}`,
  };

  try {
    const response = await fetch(transport.url, {
      body: JSON.stringify(payload),
      headers: {
        Authorization: `Bearer ${transport.bearer}`,
        "Content-Type": "application/json",
      },
      method: "POST",
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Failed to emit analytics event:", error);
    throw error;
  }
};

const prepareMessage = (message: any) => {
  const state = store.getState();
  let messageAux = message;
  if (state.config.internal.deviceId) {
    messageAux = { ...message, device_id: state.config.internal.deviceId };
  }
  return JSON.stringify(messageAux);
};
